/**
 * Organization-related type definitions for the RBAC system.
 *
 * These types correspond to the database tables:
 * - organizations
 * - org_billing
 * - org_members
 * - org_roles
 * - org_invites
 * - org_permissions
 *
 * @module lib/org-types
 */

/** Organization record from the `organizations` table. */
export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  website_url: string | null;
  settings: Record<string, unknown> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** Reason a grace period is active on an org. */
export type GraceReason = 'seat_limit' | 'payment';

/** Billing record from the `org_billing` table. */
export interface OrgBilling {
  organization_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  plan_id: string | null;
  subscription_status: string | null;
  /** Stripe trial end; current access expires only while subscription_status is trialing. */
  trial_ends_at?: string | null;
  max_seats: number;
  /** ISO timestamp when the grace period ends, or null when no grace is active. */
  grace_period_ends_at: string | null;
  /** Why the grace period started (seat_limit or payment), or null. */
  grace_reason: GraceReason | null;
  created_at: string;
  updated_at: string;
}

/** Role record from the `org_roles` table. */
export interface OrgRole {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
}

/** Member record from the `org_members` table, joined with role info. */
export interface OrgMember {
  id: string;
  organization_id: string;
  user_id: string;
  role_id: string;
  joined_at: string;
  /** Joined from org_roles */
  org_roles?: OrgRole;
  /** Joined user email from auth.users (via RPC or view) */
  email?: string;
}

/** Invite record from the `org_invites` table. */
export interface OrgInvite {
  id: string;
  organization_id: string;
  email: string;
  role_id: string;
  invited_by: string;
  token: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at: string;
  created_at: string;
}

/** Permission record from the `org_permissions` table. */
export interface OrgPermission {
  id: string;
  key: string;
  resource: string;
  action: string;
  description: string | null;
}

/** User's membership in an org, enriched with org details. */
export interface UserOrgMembership {
  /** org_members.id */
  id: string;
  organization_id: string;
  user_id: string;
  role_id: string;
  joined_at: string;
  /** Joined organization record */
  organizations: Organization;
  /** Joined role record */
  org_roles: OrgRole;
}

/** localStorage key for persisting the user's last active org. */
export const ACTIVE_ORG_STORAGE_KEY = 'holito_active_org_id';

/**
 * localStorage key for persisting the user's last active workspace id.
 *
 * Cleared whenever the active organization changes so a workspace from
 * org A cannot leak into org B's session.
 */
export const ACTIVE_WORKSPACE_STORAGE_KEY = 'holito_active_workspace_id';

/**
 * A holito Workspace. Phase A of the Workspaces feature stores rows in the
 * legacy `public.brands` table; the TypeScript surface uses the post-rename
 * `Workspace` shape so call sites do not change when Phase B renames the
 * table to `public.workspaces`.
 *
 * Hierarchy: `Organization → Workspace → Conversation (campaign) → Responses`.
 *
 * Brand assets (`logo_url`, `icon_url`) live on a private Cloudflare R2
 * bucket (`workspace-assets` / `workspace-assets-staging`). The columns
 * persist the **R2 object key** (e.g. `workspace-1/logo.png`), not a public
 * URL - bytes are served to the browser by the Pages Function
 * `functions/api/workspace-assets/[workspaceId]/[kind].ts`. Either column
 * may be null; renderers fall back to whichever variant is present, and
 * compose the browser-fetchable URL via
 * `components/workspace/assetUrl.ts → workspaceAssetUrl(...)`.
 *
 * Legacy fields from the underlying `brands` table that are NOT part of the
 * post-rename contract (e.g. `logo_path`, `owner_id`) are intentionally
 * omitted from this interface - Phase B will drop them.
 */
export interface Workspace {
  /** Workspace primary key (uuid). Maps to `brands.id` in Phase A. */
  id: string;
  /** Owning organization id. Foreign key to `organizations.id`. */
  organization_id: string;
  /** Human-readable workspace name (e.g. "Acme Marketing"). */
  name: string;
  /** URL-friendly identifier, unique per organization. Null when unassigned. */
  slug: string | null;
  /** Primary website URL associated with the workspace. */
  website_url: string | null;
  /** Safe HTTP(S) privacy policy URL. Null when none was found or supplied. */
  privacy_policy_url?: string | null;
  /** R2 object key (e.g. `workspace-1/logo.png`) of the wide logo variant.
   * Resolve to a browser-fetchable URL via `workspaceAssetUrl(workspace, 'logo')`. */
  logo_url: string | null;
  /** R2 object key (e.g. `workspace-1/icon.png`) of the square icon variant.
   * Resolve to a browser-fetchable URL via `workspaceAssetUrl(workspace, 'icon')`. */
  icon_url: string | null;
  /** Optional accent color (hex string) used by the space marker and respondent conversation. */
  color: string | null;
  /** Exactly one workspace per org has `is_default = true`. */
  is_default: boolean;
  /** AI-generated workspace summary used by the conversation-generation pipeline. */
  summary: string | null;
  /** AI-derived description of the target audience. */
  target_audience: string | null;
  /** Brand voice persona (e.g. "friendly", "authoritative"). */
  brand_voice: string | null;
  /** Brand tone modifier (e.g. "playful", "formal"). */
  brand_tone: string | null;
  /** Core value proposition statement used in conversation copy. */
  value_proposition: string | null;
  /** Topical keywords used to seed objective suggestions. */
  keywords: string[] | null;
  /**
   * Cached objective-suggestion cards rendered by the form-builder wizard.
   * Written by the brand-research worker; the wizard never calls the LLM.
   * NULL means the cache has never been populated.
   */
  suggested_objectives: SuggestedObjectiveCacheEntry[] | null;
  /**
   * ISO timestamp the `suggested_objectives` cache was last (re)written.
   * Updated atomically with `suggested_objectives`.
   */
  suggested_objectives_generated_at: string | null;
  /**
   * Exact-case brand name as the brand uses it (e.g. "holito" lowercase).
   * Populated by the brand-research v2 pipeline and fed verbatim to the AI
   * host so it never re-cases the name. NULL until research completes.
   */
  brand_name_canonical: string | null;
  /**
   * Unified knowledge graph of products and services as
   * `[{name, description}]`. The AI host treats them interchangeably. NULL
   * until research completes.
   */
  offerings: BrandOffering[] | null;
  /**
   * Array of voice traits with examples `[{trait, example}]` populated by
   * the research pipeline. Fed to the AI host; no UI in v1.
   */
  voice_attributes: BrandVoiceAttribute[] | null;
  /**
   * Array of tone traits with examples `[{trait, example}]`. Fed to the AI
   * host; no UI in v1.
   */
  tone_attributes: BrandVoiceAttribute[] | null;
  /**
   * Style guide
   * `{ formality, sentence_length, jargon_level, do_use[], avoid[] }`. Fed
   * to the AI host; no UI in v1.
   */
  style_guide: BrandStyleGuide | null;
  /**
   * Brand color profile `{ primary, secondary }` as hex strings. Two slots
   * only. v2 ships an editor.
   */
  colors: BrandColors | null;
  /**
   * Brand fonts `{ primary, secondary }`. Captured now for future
   * landing-page generation; no UI in v1.
   */
  fonts: BrandFonts | null;
  /**
   * Research lifecycle status. Drives WorkspaceSettings UI gating and
   * worker idempotency.
   * - `idle`: no run has ever started for this workspace.
   * - `in_progress`: a run is currently writing fields.
   * - `completed`: most recent run finished successfully.
   * - `failed`: most recent run failed.
   */
  research_status: BrandResearchStatus;
  /**
   * ISO timestamp the most recent research run started. Set when the
   * worker flips `research_status` to `in_progress`. NULL when no run has
   * started.
   */
  research_started_at: string | null;
  /**
   * ISO timestamp the most recent research run completed (success or
   * failure). NULL while the first run is still pending.
   */
  research_completed_at: string | null;
  /** ISO timestamp when the row was created. */
  created_at: string;
  /** ISO timestamp of the last update. */
  updated_at: string;
}

/**
 * One entry in `Workspace.offerings`. Products and services are unified into
 * a single knowledge-graph list - the AI host treats both interchangeably.
 *
 * Persistence note: stored as a `jsonb` array on `public.brands.offerings`.
 */
export interface BrandOffering {
  /** Display name, e.g. "Pro plan" or "Onboarding consultation". */
  name: string;
  /** One-sentence description used by the AI host when introducing it. */
  description: string;
}

/**
 * One trait for `Workspace.voice_attributes` or `Workspace.tone_attributes`.
 * `example` is optional but strongly preferred - concrete phrases the brand
 * actually uses help the AI host mimic register without paraphrasing the
 * trait label back at the user.
 */
export interface BrandVoiceAttribute {
  /** Trait label, e.g. "warm", "irreverent", "matter-of-fact". */
  trait: string;
  /** Optional concrete example phrase the brand actually uses. */
  example?: string;
}

/**
 * Structured style guide derived during research. Fed verbatim to the AI
 * host's system prompt; no UI in v1.
 *
 * Persistence note: stored as a `jsonb` object on
 * `public.brands.style_guide`.
 */
export interface BrandStyleGuide {
  /** Overall register: `casual` | `neutral` | `formal`. */
  formality: 'casual' | 'neutral' | 'formal';
  /** Preferred sentence length: `short` | `medium` | `long`. */
  sentence_length: 'short' | 'medium' | 'long';
  /** Industry-jargon density: `plain` | `industry` | `technical`. */
  jargon_level: 'plain' | 'industry' | 'technical';
  /** Optional list of phrases / patterns the brand prefers. */
  do_use?: string[];
  /** Optional list of phrases / patterns the brand avoids. */
  avoid?: string[];
}

/**
 * Brand color profile populated by the research pipeline. Exactly two slots
 * - `primary` and `secondary` - both hex strings (e.g. `#7c3aed`). Either
 * may be missing when research is uncertain; never fabricated.
 */
export interface BrandColors {
  /** Primary brand color as a hex string, e.g. `#7c3aed`. */
  primary?: string;
  /** Secondary brand color as a hex string. */
  secondary?: string;
}

/**
 * Brand fonts captured during research for future landing-page generation.
 * Captured now, surfaced in v2.
 */
export interface BrandFonts {
  /** Primary font family, e.g. "Inter". */
  primary?: string;
  /** Secondary font family. */
  secondary?: string;
}

/**
 * Lifecycle of a brand-research run.
 *
 * - `idle`: no run has ever started for this workspace.
 * - `in_progress`: a run is currently writing fields; the worker uses this
 *   for idempotency (a second concurrent POST returns 409).
 * - `completed`: most recent run finished successfully.
 * - `failed`: most recent run failed. The public failure code stays
 *   server-side (`brands.research_error`) and is not loaded by the client.
 */
export type BrandResearchStatus =
  | 'idle'
  | 'in_progress'
  | 'completed'
  | 'failed';

/**
 * One cached objective-suggestion card. Mirrors
 * {@link import('./suggest-objectives').SuggestedObjective} as a structural
 * type to keep `org-types.ts` import-cycle-free with `lib/suggest-objectives.ts`.
 *
 * Stored as a `jsonb` array on `public.brands`. The Gemini extractor's
 * `responseSchema` constrains `templateKey` to the known whitelist so
 * unknown values cannot reach this column.
 */
export interface SuggestedObjectiveCacheEntry {
  title: string;
  hint: string;
  templateKey: string;
  /** Optional curated template identity retained with the cached suggestion. */
  templateId?: string;
}

/**
 * Discriminated union representing the user's onboarding state.
 *
 * - `'none'`: User has orgs, no onboarding needed.
 * - `'create-org'`: Zero memberships, user must create their first org.
 *
 * Designed for extensibility - future variants (e.g. `'pending-invites'`)
 * can be added without rewriting existing consumers.
 */
export type OnboardingState =
  | { status: 'none' }
  | { status: 'create-org' };

/**
 * Steps the user must still complete in their account setup.
 *
 * Ordered steps rendered sequentially by SetupFlow. Extensible - future
 * steps (e.g. `'team-invite'`) can be added without breaking existing
 * callers.
 *
 * - `'trial-activated'` is a celebratory post-create-org step shown once to
 *   first-time org creators whose `createOrg` call returned trial details.
 *   Transient session state - not persisted.
 */
export type SetupStep = 'profile' | 'create-org' | 'trial-activated';

/**
 * Trial details returned by the metering service when a new Pro trial is
 * activated during org creation. Surfaced to the onboarding UI so the
 * success step can render without an extra round trip.
 */
export interface ActivatedTrial {
  /** Stripe Subscription id created for the trial. */
  subscriptionId: string;
  /** Unix timestamp (seconds) when the trial started. */
  trialStart: number | null;
  /** Unix timestamp (seconds) when the trial ends. */
  trialEnd: number | null;
}

/**
 * Derived state describing an active grace period on an organization.
 * When `endsAt` is null, no grace period is currently active.
 */
export interface GraceState {
  endsAt: string | null;
  reason: GraceReason | null;
}
